import React from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { useFeatures } from '@atc/features';

import {
    Box,
    BoxGrid,
} from 'reaxl';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import userDuck from '@/ducks/userDuck.js';

import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import LazyComponent from '@/components/LazyComponent';
import SponsoredInsuranceContainer from '@/components/vehicledetails/SponsoredInsuranceContainer';

// TODO: Remove after Root sponsorship is done, this is only available for users/dealers in these states
const eligibleStates = ['AL', 'AZ', 'AR', 'CO', 'DE', 'GA', 'IN', 'IA', 'IL', 'KS', 'KY', 'LA', 'MD', 'MN', 'MS', 'MO', 'NE', 'NM', 'OH', 'OK', 'OR', 'PA', 'SC', 'TN', 'TX', 'UT', 'VA', 'WV', 'WI'];

const getIsStateEligible = (userState, dealerState) => {
    if (userState) {
        return eligibleStates?.includes(userState);
    }

    return eligibleStates?.includes(dealerState);
};

const PreQualCTAContainer = dynamic(() => import('@/containers/vdp/PreQualCTAContainer'), { ssr: false });

/**
 * LenderPreQualWrapper - Renders financing and insurance pre-qualification components
 *
 * This component conditionally displays sponsored insurance containers and pre-qualification
 * CTAs based on feature flags, user eligibility, and state requirements. The component
 * supports both legacy and redesigned UI contexts.
 *
 * @param {Object} props - Component props
 * @param {Object} props.listing - Vehicle listing data containing inventory information
 * @param {string} props.listing.id - Unique identifier for the listing
 * @param {boolean} [props.listing.insurancePartnerEnabled] - Whether insurance partner is enabled for this listing
 * @param {string} [props.zip] - User's ZIP code for insurance eligibility
 *
 * @returns {React.ReactElement|null} Rendered component with financing/insurance options or null if no eligible items
 */
export function LenderPreQualWrapper({
    listing,
    zip,
}) {

    const logoMap = {
        root: 'root_ins_logo.svg',
    };

    const {
        nds_sponsored_insurance: [enableNdsSponsoredInsurance, {
            external_host: externalHost,
            zip_param: zipParam,
            vin_param: vinParam,
            sponsor_name: sponsorName,
        } = {}] = [],
        vdp_new_price_section: [enableVdpNewPriceSection] = [],
    } = useFeatures([
        'nds_sponsored_insurance',
        'vdp_new_price_section',
    ]);

    const {
        isPreQualEligible = false,
    } = usePreQualEligibility(listing);

    const userLocation = useSelector(userDuck.selectors.getLocation);
    const inventory = useSelector(inventoryDuck.selectors.selectHydratedInventory);
    const owner = inventory[listing?.id]?.owner;
    const ownerLocation = owner?.location?.address;

    const isStateEligible = getIsStateEligible(userLocation?.state, ownerLocation?.state);
    const showInsurancePartner = enableNdsSponsoredInsurance && listing?.insurancePartnerEnabled && isStateEligible;

    const wrapWithBox = (content, key) => {
        if (enableVdpNewPriceSection) {
            return content;
        }
        return (
            <Box
                key={key}
                backgroundColor="neutral-secondary"
                className="rounded"
                border
            >
                {content}
            </Box>
        );
    };

    const renderInsuranceContainer = () => {
        if (!zip || !showInsurancePartner) return null;

        const insuranceProps = {
            externalHost,
            logoMap,
            sponsorName,
            vinParam,
            zip,
            zipParam,
            ...listing,
        };

        const content = (
            <SponsoredInsuranceContainer
                key="sponsoredInsuranceContainer"
                uiContext={enableVdpNewPriceSection ? 'price-section' : null}
                {...insuranceProps}
            />
        );

        return wrapWithBox(content, 'sponsored-insurance-box');
    };

    const renderPreQualContainer = () => {
        if (!isPreQualEligible) return null;

        let uiContext;
        if (enableVdpNewPriceSection) {
            uiContext = 'price-section';
        } else if (showInsurancePartner) {
            uiContext = 'section';
        } else {
            uiContext = 'styled';
        }

        const content = (
            <LazyComponent
                key="prequal_cta_wrapper"
                fallback={null}
            >
                <PreQualCTAContainer uiContext={uiContext} />
            </LazyComponent>
        );

        return wrapWithBox(content, 'prequal_cta_wrapper_box');
    };

    const lenderInsuranceItems = [
        renderInsuranceContainer(),
        renderPreQualContainer(),
    ].filter(Boolean);

    if (!lenderInsuranceItems.length) {
        return null;
    }

    const WrapperComponent = enableVdpNewPriceSection ? 'div' : BoxGrid;
    const wrapperProps = enableVdpNewPriceSection ? {
        className: 'display-flex flex-column gap-3',
        'data-cmp': 'lender-insurance-wrapper',
    } : {
        key: 'lender-insurance-wrapper-grid',
        'data-cmp': 'lender-insurance-wrapper',
        backgroundcolor: 'neutral-secondary',
        className: 'col-xs-12 padding-0 margin-bottom-5',
    };

    return (
        <WrapperComponent {...wrapperProps}>
            {lenderInsuranceItems}
        </WrapperComponent>
    );
}

export default LenderPreQualWrapper;
